import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = [
    'sourceRegistrationInput',
    'sourceCandidateInfo',
    'sourcePaymentsList',
    'destinationSearchInput',
    'destinationCandidateInfo',
    'transferSummary',
    'step2',
    'step3',
    'confirmButton'
  ]

  static values = {
    searchSourceUrl: String,
    searchDestinationUrl: String,
    executeTransferUrl: String,
    csrfToken: String
  }

  connect() {
    this.selectedPaymentId = null
    this.sourceCandidateId = null
    this.destinationCandidateId = null
    this.destinationCandidateData = null
  }

  searchSource() {
    const registrationNumber = this.sourceRegistrationInputTarget.value.trim()
    
    if (!registrationNumber) {
      alert('Please enter a registration number')
      return
    }

    fetch(this.searchSourceUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfTokenValue
      },
      body: JSON.stringify({ registration_number: registrationNumber })
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        alert(data.error)
        return
      }

      this.sourceCandidateId = data.candidate.id
      this.displaySourceCandidate(data.candidate)
      this.displayPayments(data.payments)
    })
    .catch(error => {
      console.error('Error:', error)
      alert('An error occurred while searching for the candidate')
    })
  }

  displaySourceCandidate(candidate) {
    this.sourceCandidateInfoTarget.innerHTML = `
      <div class="message is-info">
        <div class="message-body">
          <strong>Candidate Found:</strong><br>
          Name: ${candidate.full_name}<br>
          Registration Number: ${candidate.registration_number}<br>
          Payment Confirmed: ${candidate.payment_confirmed ? 'Yes' : 'No'}
        </div>
      </div>
    `
    this.sourceCandidateInfoTarget.style.display = 'block'
  }

  displayPayments(payments) {
    if (!payments || payments.length === 0) {
      alert('No payments found for this candidate')
      return
    }

    let paymentsHtml = '<h4 class="title is-6">Select Payment to Transfer:</h4><div class="table-container"><table class="table is-fullwidth"><thead><tr><th>Select</th><th>Order ID</th><th>Status</th><th>Amount</th><th>Date</th><th>Type</th></tr></thead><tbody>'
    
    payments.forEach(payment => {
      if (payment.is_basket) {
        paymentsHtml += `<tr><td colspan="6" class="has-text-centered"><em>Basket payments cannot be transferred</em></td></tr>`
      } else {
        const statusClass = payment.status === 'success' ? 'is-success' : 'is-danger'
        const date = new Date(payment.created_at).toLocaleDateString()
        paymentsHtml += `
          <tr>
            <td>
              <input type="radio" 
                     name="payment_select" 
                     value="${payment.id}" 
                     data-action="change->payment-transfer#selectPayment"
                     data-payment-id="${payment.id}">
            </td>
            <td>${payment.order_id}</td>
            <td><span class="tag ${statusClass}">${payment.status}</span></td>
            <td>${payment.inscription_fee} XOF</td>
            <td>${date}</td>
            <td>Individual</td>
          </tr>
        `
      }
    })
    
    paymentsHtml += '</tbody></table></div>'
    this.sourcePaymentsListTarget.innerHTML = paymentsHtml
    this.sourcePaymentsListTarget.style.display = 'block'
  }

  selectPayment(event) {
    if (event.target.checked) {
      this.selectedPaymentId = event.target.dataset.paymentId
      this.step2Target.style.display = 'block'
    }
  }

  searchDestination() {
    const searchTerm = this.destinationSearchInputTarget.value.trim()
    
    if (!searchTerm) {
      alert('Please enter a search term or candidate ID')
      return
    }

    fetch(this.searchDestinationUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfTokenValue
      },
      body: JSON.stringify({ search_term: searchTerm })
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        alert(data.error)
        return
      }

      this.destinationCandidateId = data.candidate.id
      this.destinationCandidateData = data.candidate
      this.displayDestinationCandidate(data.candidate)
      
      // Only show transfer summary if candidate can receive payment
      if (data.candidate.can_receive_payment) {
        this.displayTransferSummary(data.candidate)
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('An error occurred while searching for the destination candidate')
    })
  }

  displayDestinationCandidate(candidate) {
    let messageClass = 'is-success'
    let warningMessage = ''
    
    if (!candidate.can_receive_payment) {
      messageClass = 'is-warning'
      if (!candidate.has_complete_dossier) {
        warningMessage = '<br><strong class="has-text-danger">⚠ Warning: This candidate does not have a complete dossier (current step: ' + candidate.current_step + '). The candidate must complete all required steps before receiving a payment.</strong>'
      }
      if (candidate.has_payment) {
        warningMessage = '<br><strong class="has-text-danger">⚠ Warning: This candidate already has a successful payment. A candidate cannot receive multiple payments.</strong>'
      }
    }
    
    this.destinationCandidateInfoTarget.innerHTML = `
      <div class="message ${messageClass}">
        <div class="message-body">
          <strong>Candidate Found:</strong><br>
          Name: ${candidate.full_name}<br>
          Registration Number: ${candidate.registration_number || 'Not assigned yet'}<br>
          Payment Confirmed: ${candidate.payment_confirmed ? 'Yes' : 'No'}<br>
          Has Payments: ${candidate.has_payments ? 'Yes' : 'No'}<br>
          Current Step: ${candidate.current_step}${warningMessage}
        </div>
      </div>
    `
    this.destinationCandidateInfoTarget.style.display = 'block'
    
    // Only show step 3 if candidate can receive payment
    if (candidate.can_receive_payment) {
      this.step3Target.style.display = 'block'
    }
  }

  displayTransferSummary(destinationCandidate) {
    const sourceInfo = this.sourceCandidateInfoTarget.querySelector('.message-body').textContent
    const selectedRadio = this.sourcePaymentsListTarget.querySelector('input[name="payment_select"]:checked')
    const paymentRow = selectedRadio.closest('tr')
    const paymentInfo = paymentRow.textContent

    this.transferSummaryTarget.innerHTML = `
      <div class="message is-warning">
        <div class="message-body">
          <strong>Transfer Summary:</strong><br><br>
          <strong>From:</strong> ${sourceInfo}<br><br>
          <strong>Payment:</strong> ${paymentInfo}<br><br>
          <strong>To:</strong> ${destinationCandidate.full_name} (ID: ${destinationCandidate.id})
        </div>
      </div>
    `
  }

  confirmTransfer() {
    if (!this.selectedPaymentId || !this.sourceCandidateId || !this.destinationCandidateId) {
      alert('Please complete all steps before confirming')
      return
    }

    // Check if destination candidate can receive payment
    if (this.destinationCandidateData && !this.destinationCandidateData.can_receive_payment) {
      let reason = ''
      if (!this.destinationCandidateData.has_complete_dossier) {
        reason = 'The candidate does not have a complete dossier (current step: ' + this.destinationCandidateData.current_step + ').'
      } else if (this.destinationCandidateData.has_payment) {
        reason = 'The candidate already has a successful payment.'
      }
      alert('Cannot transfer payment: ' + reason + ' Please select a different candidate.')
      return
    }

    if (!confirm('Are you absolutely sure you want to transfer this payment? This action cannot be undone.')) {
      return
    }

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.executeTransferUrlValue
    
    const csrfToken = document.createElement('input')
    csrfToken.type = 'hidden'
    csrfToken.name = 'authenticity_token'
    csrfToken.value = this.csrfTokenValue
    form.appendChild(csrfToken)

    const paymentId = document.createElement('input')
    paymentId.type = 'hidden'
    paymentId.name = 'payment_id'
    paymentId.value = this.selectedPaymentId
    form.appendChild(paymentId)

    const sourceId = document.createElement('input')
    sourceId.type = 'hidden'
    sourceId.name = 'source_candidate_id'
    sourceId.value = this.sourceCandidateId
    form.appendChild(sourceId)

    const destId = document.createElement('input')
    destId.type = 'hidden'
    destId.name = 'destination_candidate_id'
    destId.value = this.destinationCandidateId
    form.appendChild(destId)

    document.body.appendChild(form)
    form.submit()
  }
}

